import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const Navbar = () => {
  const { user, logout, adminMode, isAdminMode, toggleAdminMode, canToggleAdmin, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/reality-garden', label: 'Reality Garden', icon: '🌱' },
    { path: '/meditation-timer', label: '20min', icon: '🧘' },
    { path: '/groves', label: 'Groves', icon: '🌳' },
    { path: '/create-post', label: 'Say Something', icon: '💬' },
    { path: user?.username ? `/${user.username}` : '/my-life', label: 'MY LIFE', icon: '✨' }
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-emerald-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16">
          {/* Logo - left aligned */}
          <div className="flex-1 flex justify-start">
            <div 
              className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate('/dashboard')}
            >
              <span className="text-lg sm:text-xl font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent hidden sm:block">
                Modern Human Being
              </span>
              <span className="text-base font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent sm:hidden">
                MHB
              </span>
            </div>
          </div>

          {/* Desktop Navigation - centered */}
          <div className="hidden md:flex items-center justify-center space-x-4 lg:space-x-8">
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors min-h-[44px] ${
                  location.pathname === item.path
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'text-gray-600 hover:text-emerald-600 hover:bg-emerald-50'
                }`}
              >
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </div>

          {/* User Menu & Mobile Controls - right aligned */}
          <div className="flex-1 flex items-center justify-end space-x-2 sm:space-x-4">
            <div className="relative">
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center space-x-2 p-2 rounded-full hover:bg-emerald-100 transition-colors min-h-[44px] min-w-[44px]"
              >
                {user?.avatar ? (
                  <img 
                    src={user.avatar} 
                    alt={user.username}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {user?.username?.[0]?.toUpperCase() || 'U'}
                    </span>
                  </div>
                )}
                <span className="hidden sm:block text-gray-700 font-medium hover:text-emerald-600 transition-colors text-sm lg:text-base">{user?.username}</span>
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* User Dropdown Menu - Mobile Optimized */}
              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  <button
                    onClick={() => {
                      navigate('/profile');
                      setIsUserMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 flex items-center space-x-2 min-h-[44px]"
                  >
                    <span>👤</span>
                    <span>Profile Settings</span>
                  </button>
                  
                  {/* Admin Dashboard (only for admins) */}
                  {canToggleAdmin && (
                    <>
                      <button
                        onClick={() => {
                          navigate('/admin');
                          setIsUserMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 text-gray-700 hover:bg-purple-50 hover:text-purple-600 flex items-center space-x-2 min-h-[44px]"
                      >
                        <span>⚙️</span>
                        <span>Admin Dashboard</span>
                      </button>
                      
                      {/* Conscious Commerce - Only for the super-admin */}
                      {canToggleAdmin && (
                        <button
                          onClick={() => {
                            navigate('/admin/conscious-commerce');
                            setIsUserMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-3 text-gray-700 hover:bg-purple-50 hover:text-purple-600 flex items-center space-x-2 min-h-[44px]"
                        >
                          <span>🛒</span>
                          <span>Conscious Commerce</span>
                        </button>
                      )}
                    </>
                  )}
                  
                  {/* Admin Toggle (only for admins) */}
                  {canToggleAdmin && (
                    <>
                      <hr className="my-2 border-gray-200" />
                      <div className="px-4 py-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <span>{isAdminMode ? '👑' : '👤'}</span>
                            <span className="text-gray-700 font-medium text-sm">
                              {isAdminMode ? 'Admin Mode' : 'User Mode'}
                            </span>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={adminMode}
                              onChange={toggleAdminMode}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                          </label>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {isAdminMode ? 'See admin features & controls' : 'Experience as regular user'}
                        </div>
                      </div>
                    </>
                  )}
                  
                  <hr className="my-2 border-gray-200" />
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsUserMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 flex items-center space-x-2 min-h-[44px]"
                  >
                    <span>🚪</span>
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-full hover:bg-emerald-100 transition-colors min-h-[44px] min-w-[44px]"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation - Touch Optimized */}
        {isMenuOpen && (
          <div className="md:hidden pb-4 border-t border-emerald-200 mt-4">
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setIsMenuOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-4 rounded-lg transition-colors text-left min-h-[44px] ${
                  location.pathname === item.path
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'text-gray-600 hover:text-emerald-600 hover:bg-emerald-50'
                }`}
              >
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;